import { useState } from "react";
import { Alert } from "@/components/alert";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { Tooltip } from "@/components/tooltip";
import {
  ConfidenceCard,
  CountryCard,
  DataSourceCard,
  DobCard,
  EntityTypeCard,
  type EntityType,
} from "@/components/filter-cards";
import { addMonitoring } from "@/src/services/monitoring_service";
import { validateIdentifier } from "@/app/(app)/_lib/validate-identifier";
import { validateDob } from "@/app/(app)/_lib/validate-dob";
import { captureEvent } from "@/src/lib/posthog";
import { useDataSources } from "@/components/watchlists";
import {
  AdverseMediaEntityTypeHint,
  AdverseMediaNameRequiredHint,
  AdverseMediaSettingsHint,
} from "@/app/(app)/_components/adverse-media-info";
import {
  ADVERSE_MEDIA_COLLECTION_ID,
  ADVERSE_MEDIA_RESTRICTED_ENTITY_TYPES,
  ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP,
  buildAdverseMediaTooltip,
  useCompanyAdverseMedia,
} from "@/app/(app)/_components/adverse-media-info-utils";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  adverseMediaEnabled?: boolean;
  pepAllowed?: boolean;
};

const DEFAULT_CONFIDENCE = 88;
const MAX_NAME_LENGTH = 100;

function validateName(value: string): string | null {
  if (value.trim().length > MAX_NAME_LENGTH) {
    return `Name must be at most ${MAX_NAME_LENGTH} characters`;
  }
  return null;
}

export function NewMonitoringModal({
  open,
  onClose,
  onAdded,
  adverseMediaEnabled = true,
  pepAllowed = true,
}: Props) {
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [externalIdentifier, setExternalIdentifier] = useState("");
  const [watchlists, setWatchlists] = useState<string[]>(["coll:sanctions-lists"]);
  const [countries, setCountries] = useState<string[]>([]);
  const [dob, setDob] = useState("");
  const [dobTouched, setDobTouched] = useState(false);
  const [entityTypes, setEntityTypes] = useState<EntityType[]>(["Individual", "Organization"]);
  const [confidenceScore, setConfidenceScore] = useState(DEFAULT_CONFIDENCE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const { items: dataSourceItems } = useDataSources();
  const { companyAdverseMedia, companyConfigLoaded, hasCompanyAdverseMediaSettings } =
    useCompanyAdverseMedia();
  const adverseMediaSelected = watchlists.includes(ADVERSE_MEDIA_COLLECTION_ID);
  // Identifier/DOB are only truly dead weight once Adverse Media is the ONLY
  // watchlist — mixed with others, they still drive the other lists' search.
  const adverseMediaOnly =
    watchlists.length > 0 && watchlists.every((id) => id === ADVERSE_MEDIA_COLLECTION_ID);
  const adverseMediaEntityTypeConflict =
    adverseMediaSelected &&
    entityTypes.some((t) =>
      (ADVERSE_MEDIA_RESTRICTED_ENTITY_TYPES as readonly string[]).includes(t),
    );
  // Adverse Media has no identifier-search capability at all — a name is
  // required whenever it's selected, even mixed in with other watchlists.
  const adverseMediaNameError = adverseMediaSelected && !name.trim();

  const dobError = dobTouched ? validateDob(dob) : null;
  const filled = name.length > 0;

  const nameOrIdValid = name.trim().length > 0 || identifier.trim().length > 0;
  const watchlistValid = watchlists.length > 0;
  const identifierError = validateIdentifier(identifier);
  const nameError = validateName(name);

  async function handleSubmit() {
    if (
      !nameOrIdValid ||
      !watchlistValid ||
      adverseMediaEntityTypeConflict ||
      adverseMediaNameError ||
      identifierError ||
      nameError
    ) {
      setShowErrors(true);
      return;
    }

    setDobTouched(true);
    if (dob && validateDob(dob)) return;

    setSubmitError(null);
    setIsSubmitting(true);
    const countriesForApi = countries;
    const hasCountries = countriesForApi.length > 0;
    const entityType = entityTypes.length === 1 ? entityTypes[0] : undefined;
    const res = await addMonitoring({
      name: name.trim(),
      externalIdentifier: externalIdentifier.trim() || undefined,
      identifier: identifier.trim() || undefined,
      dob: dob.trim() || undefined,
      countries: hasCountries ? countriesForApi : undefined,
      entityType: entityType ?? undefined,
      watchlists: watchlists.length > 0 ? watchlists : undefined,
      confidenceScore,
    });
    setIsSubmitting(false);

    if (res.ok) {
      captureEvent("Add monitoring entry", {
        has_name: Boolean(name.trim()),
        has_identifier: Boolean(identifier.trim()),
        has_dob: Boolean(dob),
        entity_type: entityType ?? null,
        data_sources: watchlists.join(","),
        countries: countriesForApi.join(","),
        confidence_score: confidenceScore,
        source: "monitoring",
      });
      onAdded();
    } else {
      setSubmitError(res.error.message || "Couldn't add the name. Please try again.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a name to monitor"
      description="Enter the details of the name you want to monitor."
      width={960}
      padding={24}
    >
      <div className="flex flex-col gap-4">
        <div className="@container flex w-full flex-col gap-2 rounded-sm border border-grey-200 bg-grey-50 p-2">
          <form
            id="add-monitoring-form"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            className="flex w-full flex-col gap-2"
          >
            <div className="flex h-11 w-full min-w-0 items-center rounded-sm border border-grey-200 bg-white px-4 shadow-flat transition-colors focus-within:border-grey-500">
              <input
                type="text"
                value={externalIdentifier}
                onChange={(e) => setExternalIdentifier(e.target.value)}
                placeholder="External ID (CUST-...)"
                aria-label="External identifier"
                className="min-w-0 flex-1 border-0 bg-transparent text-body text-grey-900 outline-none placeholder:text-grey-600"
              />
            </div>
            <div className="flex w-full items-center gap-2">
              <div
                className={[
                  "flex h-11 min-w-0 flex-1 items-center rounded-sm border bg-white px-4 shadow-flat transition-colors",
                  (showErrors && !nameOrIdValid) ||
                  (showErrors && adverseMediaNameError) ||
                  (showErrors && nameError)
                    ? "border-red-500"
                    : [
                        filled ? "border-grey-500" : "border-grey-200",
                        "focus-within:border-grey-500",
                      ].join(" "),
                ].join(" ")}
              >
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a name to monitor..."
                  aria-label="Name to monitor"
                  autoFocus
                  className="min-w-0 flex-1 border-0 bg-transparent text-body text-grey-900 outline-none placeholder:text-grey-600"
                />
              </div>
              <Tooltip
                title={adverseMediaOnly ? ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP : undefined}
                wrapperClassName="flex-1 min-w-0"
              >
                <div
                  className={[
                    "flex h-11 min-w-0 flex-1 items-center rounded-sm border bg-white px-4 shadow-flat transition-colors",
                    adverseMediaOnly ? "bg-grey-100" : "",
                    (showErrors && !nameOrIdValid) || (showErrors && identifierError)
                      ? "border-red-500"
                      : [
                          identifier ? "border-grey-500" : "border-grey-200",
                          "focus-within:border-grey-500",
                        ].join(" "),
                  ].join(" ")}
                >
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Identifier (passport / national ID)..."
                    aria-label="Identifier"
                    disabled={adverseMediaOnly}
                    className="min-w-0 flex-1 border-0 bg-transparent text-body text-grey-900 outline-none placeholder:text-grey-600 disabled:cursor-not-allowed disabled:text-grey-500"
                  />
                </div>
              </Tooltip>
            </div>
          </form>
          {showErrors && !nameOrIdValid && (
            <p role="alert" className="px-1 text-tiny font-medium text-red-600">
              Enter a name or identifier to monitor.
            </p>
          )}
          {showErrors && nameOrIdValid && adverseMediaNameError && <AdverseMediaNameRequiredHint />}
          {showErrors && nameError && (
            <p role="alert" className="px-1 text-tiny font-medium text-red-600">
              {nameError}
            </p>
          )}
          {showErrors && nameOrIdValid && identifierError && (
            <p role="alert" className="px-1 text-tiny font-medium text-red-600">
              {identifierError}
            </p>
          )}

          <div className="grid w-full grid-cols-1 gap-1 @xl:grid-cols-2 @3xl:grid-cols-3">
            <div className="flex flex-col gap-0.5">
              <DataSourceCard
                selected={watchlists}
                onChange={setWatchlists}
                items={dataSourceItems}
                label="Watchlist"
                defaultSelected={["coll:sanctions-lists"]}
                className="min-h-41 w-full flex-1"
                pepAllowed={pepAllowed}
                adverseMediaEnabled={adverseMediaEnabled}
                chipDescriptions={{
                  [ADVERSE_MEDIA_COLLECTION_ID]: buildAdverseMediaTooltip(companyAdverseMedia),
                }}
              />
              {showErrors && !watchlistValid && (
                <p role="alert" className="px-1 text-tiny font-medium text-red-600">
                  Select at least one watchlist.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <ConfidenceCard
                value={confidenceScore}
                onChange={setConfidenceScore}
                className="w-full"
              />
              <EntityTypeCard
                selected={entityTypes}
                onChange={setEntityTypes}
                defaultSelected={["Individual", "Organization"]}
                className="w-full flex-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <CountryCard selected={countries} onChange={setCountries} className="w-full" />
              <Tooltip
                title={adverseMediaOnly ? ADVERSE_MEDIA_UNUSED_FIELD_TOOLTIP : undefined}
                wrapperClassName="w-full flex-1"
              >
                <DobCard
                  value={dob}
                  onChange={setDob}
                  error={dobError ?? undefined}
                  className="w-full flex-1"
                  disabled={adverseMediaOnly}
                />
              </Tooltip>
            </div>
          </div>
        </div>

        {adverseMediaEntityTypeConflict && <AdverseMediaEntityTypeHint />}

        {companyConfigLoaded && adverseMediaSelected && adverseMediaEnabled && (
          <AdverseMediaSettingsHint configured={hasCompanyAdverseMediaSettings} />
        )}

        {submitError && (
          <Alert tone="danger" role="alert">
            {submitError}
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button hierarchy="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-monitoring-form"
            hierarchy="primary"
            disabled={isSubmitting || adverseMediaEntityTypeConflict}
          >
            {isSubmitting ? "Adding…" : "Add Monitoring"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
